import {
  useAccountUsers,
  useAddress,
  useCreateAddress,
  useCreateSite,
  useDeleteSite,
  useLocations,
  useOrganisation,
  useReplaceSiteMembers,
  useSitesWithMembers,
  useUpdateAddress,
  useUpdateSite,
} from '@my/directory-api/hooks';
import type {
  Address,
  CreateAddressRequest,
  SiteWithMembers,
  TeamMemberSummary,
  UpdateAddressRequest,
  User,
} from '@my/directory-api';
import { COUNTRIES } from '@my/platform/options';
import { useServiceTags } from '@my/tms-api/hooks';
import type { ServiceTag } from '@my/tms-api';
import {
  AddressInlineEditor,
  ConfirmDialog,
  ErrorBanner,
  Loading,
  TextInput,
  showToast,
  type SelectInputOption,
} from '@my/ui';
import {
  Check,
  Edit,
  MapPin,
  Plus,
  Trash2,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface SitesTabProps {
  /** Owning organisation id, resolved by the parent page from JWT claims. */
  organisationId: string;
}

/**
 * Sites tab — master-detail surface for site CRUD plus member assignment.
 *
 * Mirrors the Teams tab layout (cards on the left, member roster / picker
 * on the right) and adds full Add / Edit / Delete on top. Each site owns
 * a paired Address row in the directory, so create/update flows chain
 * an address mutation with a site mutation:
 *
 *   - **New**: POST `/addresses` → POST `/sites` → optional PUT
 *     `/sites/{id}/members` if the operator picked any during create.
 *   - **Edit**: PUT `/addresses` (only when address fields changed) →
 *     PUT `/sites` (only when name changed) → optional PUT
 *     `/sites/{id}/members` if the membership delta is non-empty. The
 *     diff-and-skip avoids no-op round-trips on a name-only edit.
 *   - **Delete**: DELETE `/sites/{id}` — BE soft-deletes (Site is
 *     `ISoftDelete`).
 *
 * Right-panel modes match Teams:
 *   - **No selection**: empty state inviting the operator to pick a site.
 *   - **Viewing**: read-only roster of the selected site's members.
 *   - **Editing**: every user with a TMS grant on the org listed with a
 *     checkbox bound to the editor's draft.
 */
export function SitesTab({ organisationId }: SitesTabProps) {
  const { t } = useTranslation('organisation');

  // Reads
  const sitesQuery = useSitesWithMembers(organisationId);
  const usersQuery = useAccountUsers();
  // Org payload — used to identify the "Main Office" site (the one
  // pairing with `mainOfficeAddressId`) so its editor can lock the
  // address fields. Address edits on that pair belong on the
  // Organisation page; this tab only surfaces name/code/tags for it.
  const orgQuery = useOrganisation(organisationId);
  // Address catalogue — needed to surface the address `code` on each
  // site card (the SiteWithMembers payload only carries `addressId`).
  // `useLocations` returns all Task / Location / Primary addresses for
  // the org, which covers every site's address. Already cached if the
  // operator has visited the Locations page in this session.
  const locationsQuery = useLocations(organisationId);
  // Service-tag catalogue — drives the chip row in the editor.
  // Filters down to Active tags applicable to 'Locations' (sites are
  // physical locations, so the same tag set makes sense for both
  // surfaces). Tags with empty `applyToServiceTypes` apply everywhere.
  const serviceTagsQuery = useServiceTags(organisationId);

  // Writes
  const createSiteMutation = useCreateSite();
  const updateSiteMutation = useUpdateSite();
  const deleteSiteMutation = useDeleteSite();
  const replaceMembersMutation = useReplaceSiteMembers();
  const createAddressMutation = useCreateAddress();
  const updateAddressMutation = useUpdateAddress();

  // `null` = no site selected; `'new'` = creating; guid = viewing/editing.
  const [selectedId, setSelectedId] = useState<string | 'new' | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<SiteForm>(emptyForm());
  const [submitted, setSubmitted] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SiteWithMembers | null>(null);

  const isBusy =
    createSiteMutation.isPending ||
    updateSiteMutation.isPending ||
    deleteSiteMutation.isPending ||
    replaceMembersMutation.isPending ||
    createAddressMutation.isPending ||
    updateAddressMutation.isPending;

  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);

  // Map from `addressId → code` so each site card can render its
  // address code without per-card fetches. Built from the org-wide
  // locations payload above. Empty string codes are skipped — the
  // card only shows the line when there's something useful to show.
  const codeByAddressId = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of locationsQuery.data ?? []) {
      const code = a.code?.trim();
      if (code) map.set(a.id, code);
    }
    return map;
  }, [locationsQuery.data]);

  // Active service tags applicable to 'Locations' (sites = physical
  // locations, same tag pool). Tags without `applyToServiceTypes` apply
  // everywhere. Sorted alphabetically so the chip row reads predictably.
  const applicableTags: ServiceTag[] = useMemo(() => {
    return (serviceTagsQuery.data ?? [])
      .filter((tag) => {
        if (tag.status !== 'Active') return false;
        const apply = tag.applyToServiceTypes?.trim();
        if (!apply) return true;
        return apply
          .split(',')
          .map((s) => s.trim())
          .includes('Locations');
      })
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      );
  }, [serviceTagsQuery.data]);

  // Lower-cased set for cheap chip-state lookup.
  const tagSet = useMemo(
    () =>
      new Set(
        draft.address.serviceTags
          .split(',')
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean),
      ),
    [draft.address.serviceTags],
  );

  // Members picker source — every user with a TMS grant on this org.
  // Same filter as Teams tab for picker consistency.
  const memberCandidates = useMemo<User[]>(() => {
    const all = usersQuery.data ?? [];
    return all
      .filter((u) =>
        u.accounts.some((a) =>
          a.appRoles.some(
            (r) => r.app === 'Tms' && r.organisationId === organisationId,
          ),
        ),
      )
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [usersQuery.data, organisationId]);

  const selectedSite = useMemo<SiteWithMembers | null>(() => {
    if (selectedId === null || selectedId === 'new') return null;
    return sites.find((s) => s.id === selectedId) ?? null;
  }, [selectedId, sites]);

  // True when the selected site is the org's "Main Office" — i.e. its
  // address pairs with the org's `mainOfficeAddressId`. The editor
  // locks the address fields for this site (operators edit the address
  // on the Organisation page); name / code / service tags stay editable
  // here. False for new-site mode (always full editor).
  const isOrganisationSite =
    selectedSite !== null &&
    orgQuery.data?.mainOfficeAddressId !== null &&
    orgQuery.data?.mainOfficeAddressId === selectedSite.addressId;

  // Fetch the address for the selected site so the editor can pre-fill.
  // Disabled while creating ('new') or when nothing is selected. The
  // standard 5-min staleTime means a second visit to the same site
  // doesn't re-fetch.
  const addressQuery = useAddress(selectedSite?.addressId ?? null);

  // If the selected site disappears (deleted, refresh, etc.), fall back
  // cleanly to the empty state.
  useEffect(() => {
    if (selectedId === null || selectedId === 'new') return;
    if (!sites.some((s) => s.id === selectedId)) {
      setSelectedId(null);
      setIsEditing(false);
    }
  }, [sites, selectedId]);

  // ── Action handlers ──────────────────────────────────────────────────
  const selectSite = (site: SiteWithMembers) => {
    if (isEditing) return; // single-editor rule — discard-on-cancel pattern
    setSelectedId(site.id);
    setDraft({
      memberUserIds: site.members.map((m) => m.userId),
      // Address fields populated by the address-arrival effect below
      // once `useAddress` resolves. Seed name from the list-payload
      // value so the editor doesn't flash an empty Name input on the
      // few hundred ms before the address fetch lands.
      address: { ...emptyAddress(), name: site.name },
    });
    setSubmitted(false);
  };

  const startNew = () => {
    setSelectedId('new');
    setIsEditing(true);
    setDraft(emptyForm());
    setSubmitted(false);
  };

  const startEdit = () => {
    if (!selectedSite) return;
    setIsEditing(true);
    setDraft({
      memberUserIds: selectedSite.members.map((m) => m.userId),
      address: addressQuery.data
        ? addressToForm(addressQuery.data)
        : { ...emptyAddress(), name: selectedSite.name },
    });
    setSubmitted(false);
  };

  const cancelEdit = () => {
    if (selectedId === 'new') {
      setSelectedId(null);
    }
    setIsEditing(false);
    setSubmitted(false);
  };

  // When the address arrives mid-edit (e.g. operator clicked Edit while
  // the address was still loading), backfill the draft. Only runs when
  // the address fields are still empty so we don't clobber user input.
  useEffect(() => {
    if (!isEditing || selectedId === 'new') return;
    if (!addressQuery.data) return;
    setDraft((prev) =>
      prev.address.fullAddress === '' && prev.address.countryIsoCode === ''
        ? { ...prev, address: addressToForm(addressQuery.data!) }
        : prev,
    );
  }, [addressQuery.data, isEditing, selectedId]);

  const handleSave = async () => {
    if (selectedId === null) return;
    setSubmitted(true);
    if (!isFormValid(draft)) return;

    try {
      if (selectedId === 'new') {
        // 1. Create the address row first — needed for the site's FK.
        const addressId = await createAddressMutation.mutateAsync(
          toCreateAddressRequest(organisationId, draft),
        );
        // 2. Create the site, pointing at the new address. Site name
        //    mirrors the address name so the two stay in lockstep.
        const siteId = await createSiteMutation.mutateAsync({
          organisationId,
          name: draft.address.name.trim(),
          type: 'Site',
          addressId,
          status: 'Active',
        });
        // 3. Optional: assign members if the operator picked any.
        if (draft.memberUserIds.length > 0) {
          await replaceMembersMutation.mutateAsync({
            siteId,
            organisationId,
            body: { userIds: draft.memberUserIds },
          });
        }
        showToast({ type: 'success', message: t('siteAssignments.added') });
        setSelectedId(siteId);
        setIsEditing(false);
      } else {
        // Edit existing site.
        if (!selectedSite || !addressQuery.data) return;
        const original = selectedSite;
        const originalAddress = addressQuery.data;
        const originalMemberIds = original.members.map((m) => m.userId);

        // Diff each axis; only fire the corresponding mutation when
        // something actually changed. Saves cache churn + round-trips on
        // narrow edits (e.g. just renaming).
        if (addressChanged(originalAddress, draft.address)) {
          await updateAddressMutation.mutateAsync(
            toUpdateAddressRequest(originalAddress, draft),
          );
        }
        if (original.name !== draft.address.name.trim()) {
          await updateSiteMutation.mutateAsync({
            organisationId,
            body: {
              siteId: original.id,
              name: draft.address.name.trim(),
              addressId: original.addressId,
              // Echoed from the loaded site. This form edits the name and the
              // address; the PUT replaces the whole row, so hard-coding the type
              // and status (as this did) retyped every depot as a plain Site and
              // reactivated archived ones, while the three fields it never sent
              // were simply cleared.
              type: original.type,
              parentId: original.parentId,
              capacity: original.capacity,
              amenities: original.amenities,
              status: original.status,
            },
          });
        }
        if (!sameSet(originalMemberIds, draft.memberUserIds)) {
          await replaceMembersMutation.mutateAsync({
            siteId: original.id,
            organisationId,
            body: { userIds: draft.memberUserIds },
          });
        }
        showToast({ type: 'success', message: t('siteAssignments.updated') });
        setIsEditing(false);
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('siteAssignments.saveFailed'),
      });
    }
  };

  const requestDelete = () => {
    if (!selectedSite) return;
    setPendingDelete(selectedSite);
  };

  const confirmDeletePending = async () => {
    const site = pendingDelete;
    if (!site) return;
    try {
      await deleteSiteMutation.mutateAsync({ id: site.id, organisationId });
      showToast({ type: 'success', message: t('siteAssignments.removed') });
      if (selectedId === site.id) {
        setSelectedId(null);
        setIsEditing(false);
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('siteAssignments.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  const toggleMember = (userId: string) => {
    setDraft((prev) => ({
      ...prev,
      memberUserIds: prev.memberUserIds.includes(userId)
        ? prev.memberUserIds.filter((id) => id !== userId)
        : [...prev.memberUserIds, userId],
    }));
  };

  // Add or remove a service tag from the draft's comma-joined list.
  // Case-insensitive comparison via lower-cased name (BE canonicalises
  // to the catalogue-cased name on save anyway, so we keep it simple).
  const toggleTag = (tag: ServiceTag) => {
    const lower = tag.name.toLowerCase();
    const list = draft.address.serviceTags
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const idx = list.findIndex((s) => s.toLowerCase() === lower);
    if (idx >= 0) list.splice(idx, 1);
    else list.push(tag.name);
    setDraft((prev) => ({
      ...prev,
      address: { ...prev.address, serviceTags: list.join(',') },
    }));
  };

  return (
    <section className="rounded-md border border-slate-200 bg-white">
      {/* Title is intentionally omitted — the active sub-nav entry on the
          left already names the section. The description sits inline with
          the action area, matching the Teams / Users tab convention. */}
      <header className="flex items-center justify-between gap-3 border-b border-slate-200 p-4">
        <p className="text-xs text-slate-500">{t('siteAssignments.description')}</p>
        {/* Hide + Add while an editor is open — single-editor rule. */}
        {!isEditing && (
          <button
            type="button"
            aria-label={t('siteAssignments.addAria')}
            onClick={startNew}
            disabled={isBusy || sitesQuery.isPending}
            className="flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('siteAssignments.add')}
          </button>
        )}
      </header>

      {sitesQuery.isPending && (
        <div className="p-4">
          <Loading label={t('siteAssignments.loading')} />
        </div>
      )}
      {sitesQuery.isError && (
        <div className="p-4">
          <ErrorBanner
            title={t('siteAssignments.loadFailed')}
            message={sitesQuery.error.message}
          />
        </div>
      )}

      {sitesQuery.isSuccess && (
        <div className="grid min-h-[480px] grid-cols-1 lg:grid-cols-[560px_1fr]">
          <LeftColumn
            sites={sites}
            codeByAddressId={codeByAddressId}
            selectedId={selectedId}
            isEditing={isEditing}
            draft={draft}
            onDraftChange={setDraft}
            submitted={submitted}
            addressLoading={
              isEditing && selectedId !== 'new' && addressQuery.isPending
            }
            addressError={addressQuery.error?.message ?? null}
            isOrganisationSite={isOrganisationSite}
            applicableTags={applicableTags}
            tagSet={tagSet}
            onToggleTag={toggleTag}
            onSelect={selectSite}
            onStartEdit={startEdit}
            onSave={handleSave}
            onCancel={cancelEdit}
            onRequestDelete={requestDelete}
            isBusy={isBusy}
          />
          <RightColumn
            selectedId={selectedId}
            selectedSite={selectedSite}
            isEditing={isEditing}
            memberCandidates={memberCandidates}
            selectedMemberIds={draft.memberUserIds}
            onToggleMember={toggleMember}
          />
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('siteAssignments.deleteDialog.title')}
        message={
          pendingDelete
            ? t('siteAssignments.deleteDialog.message', { name: pendingDelete.name })
            : null
        }
        confirmLabel={t('siteAssignments.deleteDialog.confirm')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={deleteSiteMutation.isPending}
      />
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Left column: site cards + inline editor
// ────────────────────────────────────────────────────────────────────────────

interface LeftColumnProps {
  sites: SiteWithMembers[];
  /** addressId → code lookup for surfacing the code on each card. */
  codeByAddressId: Map<string, string>;
  selectedId: string | 'new' | null;
  isEditing: boolean;
  draft: SiteForm;
  onDraftChange: (next: SiteForm) => void;
  submitted: boolean;
  addressLoading: boolean;
  addressError: string | null;
  /** True when the selected site is the org's main office. */
  isOrganisationSite: boolean;
  applicableTags: ServiceTag[];
  tagSet: Set<string>;
  onToggleTag: (tag: ServiceTag) => void;
  onSelect: (site: SiteWithMembers) => void;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onRequestDelete: () => void;
  isBusy: boolean;
}

function LeftColumn({
  sites,
  codeByAddressId,
  selectedId,
  isEditing,
  draft,
  onDraftChange,
  submitted,
  addressLoading,
  addressError,
  isOrganisationSite,
  applicableTags,
  tagSet,
  onToggleTag,
  onSelect,
  onStartEdit,
  onSave,
  onCancel,
  onRequestDelete,
  isBusy,
}: LeftColumnProps) {
  const { t } = useTranslation('organisation');
  const isNewMode = selectedId === 'new';

  return (
    <div className="border-b border-slate-200 lg:border-b-0 lg:border-r">
      {/*
        View mode: cap height + inner scroll so a long site list doesn't
        push the rest of the page down. Edit mode: drop the cap so the
        editor (which is much taller than a card thanks to the embedded
        map + tag chips) can display in full without a scrollbar inside
        the panel — the container just grows downward into the empty
        space already reserved by the page layout.
      */}
      <ul
        className={
          'space-y-2 p-3 ' +
          (isEditing
            ? ''
            : 'max-h-[calc(60vh+80px)] overflow-auto')
        }
      >
        {/* New-site editor at the top — same convention as Teams. */}
        {isNewMode && isEditing && (
          <li>
            <SiteEditor
              isNew
              draft={draft}
              onChange={onDraftChange}
              submitted={submitted}
              addressLoading={false}
              addressError={null}
              isOrganisationSite={false}
              applicableTags={applicableTags}
              tagSet={tagSet}
              onToggleTag={onToggleTag}
              onSave={onSave}
              onCancel={onCancel}
              isBusy={isBusy}
            />
          </li>
        )}

        {sites.length === 0 && !isNewMode && (
          <li>
            <p className="px-1 text-sm text-slate-500">
              {t('siteAssignments.empty')}
            </p>
          </li>
        )}

        {sites.map((site) => {
          const isSelected = selectedId === site.id;
          if (isSelected && isEditing) {
            // Replace the card slot with the inline editor.
            return (
              <li key={site.id}>
                <SiteEditor
                  isNew={false}
                  draft={draft}
                  onChange={onDraftChange}
                  submitted={submitted}
                  addressLoading={addressLoading}
                  addressError={addressError}
                  isOrganisationSite={isOrganisationSite}
                  applicableTags={applicableTags}
                  tagSet={tagSet}
                  onToggleTag={onToggleTag}
                  onSave={onSave}
                  onCancel={onCancel}
                  onDelete={onRequestDelete}
                  isBusy={isBusy}
                />
              </li>
            );
          }
          return (
            <li key={site.id}>
              <SiteCard
                site={site}
                code={codeByAddressId.get(site.addressId) ?? null}
                isSelected={isSelected}
                disabled={isEditing}
                onSelect={() => onSelect(site)}
                onStartEdit={onStartEdit}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface SiteCardProps {
  site: SiteWithMembers;
  /** Address code, or null if the site's address has none. */
  code: string | null;
  isSelected: boolean;
  disabled: boolean;
  onSelect: () => void;
  onStartEdit: () => void;
}

function SiteCard({
  site,
  code,
  isSelected,
  disabled,
  onSelect,
  onStartEdit,
}: SiteCardProps) {
  const { t } = useTranslation('organisation');
  return (
    <div
      className={
        'relative w-full rounded-md border p-3 text-left transition-colors ' +
        (disabled
          ? 'border-slate-200 bg-white opacity-50'
          : isSelected
            ? 'border-purple-300 bg-purple-50 shadow-sm'
            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50')
      }
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onSelect}
          disabled={disabled}
          className="block min-w-0 flex-1 text-left disabled:cursor-not-allowed"
        >
          <div className="flex min-w-0 items-center gap-2">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span className="truncate text-sm font-semibold text-slate-900">
              {site.name}
            </span>
          </div>
          {/* Address code under the name. Always rendered (with a
              non-breaking-space placeholder when empty) so cards stay
              the same height regardless of whether the site has a code
              configured — same height-locking pattern as Teams. */}
          <p
            className={
              'mt-0.5 truncate font-mono text-[11px] ' +
              (code ? 'text-slate-500' : 'select-none text-transparent')
            }
          >
            {code ?? ' '}
          </p>
        </button>
        {/* Edit-button slot is always reserved (24x24 placeholder when
            not selected) so cards keep a constant height across
            selection states. */}
        <div className="flex shrink-0 flex-col items-end gap-1">
          <MemberCountChip count={site.members.length} />
          {isSelected && !disabled ? (
            <button
              type="button"
              aria-label={t('siteAssignments.ariaEdit', { name: site.name })}
              onClick={(e) => {
                e.stopPropagation();
                onStartEdit();
              }}
              className="flex h-6 w-6 items-center justify-center rounded-sm bg-white/80 text-blue-600 ring-1 ring-inset ring-slate-200 hover:bg-white"
            >
              <Edit className="h-3 w-3" />
            </button>
          ) : (
            <div className="h-6 w-6" aria-hidden />
          )}
        </div>
      </div>
    </div>
  );
}

function MemberCountChip({ count }: { count: number }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-inset ring-slate-200">
      <UsersIcon className="h-3 w-3" />
      {count}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Right column: members view OR full users picker
// ────────────────────────────────────────────────────────────────────────────

interface RightColumnProps {
  selectedId: string | 'new' | null;
  selectedSite: SiteWithMembers | null;
  isEditing: boolean;
  memberCandidates: User[];
  selectedMemberIds: string[];
  onToggleMember: (userId: string) => void;
}

function RightColumn({
  selectedId,
  selectedSite,
  isEditing,
  memberCandidates,
  selectedMemberIds,
  onToggleMember,
}: RightColumnProps) {
  const { t } = useTranslation('organisation');

  // Empty — no site selected.
  if (selectedId === null) {
    return (
      <div className="flex items-center justify-center p-8 text-center">
        <div>
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <MapPin className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-slate-700">
            {t('siteAssignments.detail.emptyTitle')}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {t('siteAssignments.detail.emptyHint')}
          </p>
        </div>
      </div>
    );
  }

  // Edit mode — full users picker on the right.
  if (isEditing) {
    return (
      <MemberPicker
        candidates={memberCandidates}
        selected={selectedMemberIds}
        onToggle={onToggleMember}
      />
    );
  }

  // View mode — read-only roster of the selected site.
  if (!selectedSite) {
    return (
      <div className="p-4">
        <Loading label={t('siteAssignments.loading')} />
      </div>
    );
  }
  return (
    <div className="flex flex-col p-4">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
        {/* Reuses the Teams 'Members ({{count}})' string — generic enough
            for both tabs, avoids duplicating six locale files. */}
        {t('teams.detail.membersHeader', { count: selectedSite.members.length })}
      </h3>
      {selectedSite.members.length === 0 ? (
        <p className="text-xs text-slate-500">{t('teams.fields.noMembers')}</p>
      ) : (
        <ul className="max-h-[60vh] space-y-1 overflow-auto">
          {selectedSite.members.map((m) => (
            <MemberRow key={m.userId} member={m} />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Site-member row — the API returns `TeamMemberSummary` shape for both
 * tabs, so this matches the Teams tab's `MemberRow` exactly.
 */
function MemberRow({ member }: { member: TeamMemberSummary }) {
  return (
    <li className="flex items-center gap-3 rounded-sm border border-slate-200 px-3 py-2">
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-700"
        aria-hidden
      >
        {member.initials}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-900">
          {member.fullName}
        </p>
        <p className="truncate text-xs text-slate-500">{member.email}</p>
      </div>
    </li>
  );
}

interface MemberPickerProps {
  candidates: User[];
  selected: string[];
  onToggle: (userId: string) => void;
}

/**
 * Edit-mode right panel: every user with a TMS grant on this org listed
 * with a checkbox. Identical shape to the Teams tab's picker — copied
 * (not extracted) since the two tabs are stable; lift if a third caller
 * needs the same picker.
 */
function MemberPicker({ candidates, selected, onToggle }: MemberPickerProps) {
  const { t } = useTranslation('organisation');

  if (candidates.length === 0) {
    return (
      <div className="p-4">
        <p className="text-xs text-slate-500">{t('teams.fields.noMembers')}</p>
      </div>
    );
  }

  const selectedSet = new Set(selected);

  return (
    <div className="flex flex-col p-4">
      <h3 className="mb-2 flex items-center justify-between text-xs font-medium uppercase tracking-wide text-slate-500">
        <span>{t('teams.detail.pickerHeader', { count: candidates.length })}</span>
        <span className="text-[10px] normal-case text-slate-400">
          {t('teams.detail.pickerSelected', { count: selected.length })}
        </span>
      </h3>
      <ul className="max-h-[60vh] space-y-1 overflow-auto">
        {candidates.map((u) => {
          const checked = selectedSet.has(u.id);
          return (
            <li key={u.id}>
              <label
                className={
                  'flex cursor-pointer items-center gap-3 rounded-sm border px-3 py-2 transition-colors ' +
                  (checked
                    ? 'border-purple-300 bg-purple-50'
                    : 'border-slate-200 bg-white hover:bg-slate-50')
                }
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(u.id)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                />
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-700"
                  aria-hidden
                >
                  {(u.initials || u.email.slice(0, 2)).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {u.fullName || u.email}
                  </p>
                  <p className="truncate text-xs text-slate-500">{u.email}</p>
                </div>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Editor (left column, in-place)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Address-shaped draft used by the AddressInlineEditor. Mirrors the
 * `AddressInlineEditorValue` contract (name, code, address fields,
 * lat/lng) plus a service-tags string so the same draft drives both
 * the form fields and the chip row.
 *
 * `name` doubles as the site name — the BE Site row carries its own
 * name field (used in lists, member chips, etc.) but for the editor
 * surface we keep them in lockstep so operators don't have to rename
 * twice.
 *
 * `serviceTags` is comma-joined to match `Address.serviceTags` on the
 * wire — same canonical shape used by the Locations editor.
 */
interface AddressDraft {
  name: string;
  code: string;
  fullAddress: string;
  city: string;
  postcode: string;
  countryIsoCode: string;
  /** Geocoded coordinates from PlacesAutocomplete; null until verified. */
  latitude: number | null;
  longitude: number | null;
  placeId: string;
  /**
   * Where the coordinates came from: `Google` (picked / Find on map) or
   * `Manual` (pin dragged or tapped). Forwarded on save so Directory never
   * refreshes a hand-placed pin back off the gate.
   */
  coordinatesSource: 'Google' | 'Manual' | null;
  serviceTags: string;
}

interface SiteForm {
  /** Operator-picked TMS users assigned to this site. */
  memberUserIds: string[];
  /** Address shape — `address.name` IS the site name. */
  address: AddressDraft;
}

function emptyAddress(): AddressDraft {
  return {
    name: '',
    code: '',
    fullAddress: '',
    city: '',
    postcode: '',
    countryIsoCode: '',
    latitude: null,
    longitude: null,
    placeId: '',
    coordinatesSource: null,
    serviceTags: '',
  };
}

function emptyForm(): SiteForm {
  return { memberUserIds: [], address: emptyAddress() };
}

function addressToForm(address: Address): AddressDraft {
  return {
    name: address.name ?? '',
    code: address.code ?? '',
    fullAddress: address.fullAddress ?? '',
    city: address.city ?? '',
    postcode: address.postcode ?? '',
    countryIsoCode: address.countryIsoCode ?? '',
    latitude: address.latitude ?? null,
    longitude: address.longitude ?? null,
    placeId: address.placeId ?? '',
    coordinatesSource: address.coordinatesSource ?? null,
    serviceTags: address.serviceTags ?? '',
  };
}

function isFormValid(form: SiteForm): boolean {
  const name = form.address.name.trim();
  return (
    name.length > 0 &&
    name.length <= 128 &&
    form.address.fullAddress.trim().length > 0 &&
    form.address.countryIsoCode.trim().length > 0
  );
}

/** Did the operator change any address field (incl. name/code/tags)? */
function addressChanged(original: Address, draft: AddressDraft): boolean {
  return (
    (original.name ?? '') !== draft.name.trim() ||
    (original.code ?? '') !== (draft.code.trim() || '') ||
    (original.fullAddress ?? '') !== draft.fullAddress.trim() ||
    (original.city ?? '') !== (draft.city.trim() || '') ||
    (original.postcode ?? '') !== (draft.postcode.trim() || '') ||
    (original.countryIsoCode ?? '') !== draft.countryIsoCode.trim().toUpperCase() ||
    (original.latitude ?? null) !== draft.latitude ||
    (original.longitude ?? null) !== draft.longitude ||
    (original.serviceTags ?? '') !== (draft.serviceTags.trim() || '')
  );
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  for (const x of b) if (!set.has(x)) return false;
  return true;
}

/**
 * Build the POST `/addresses` body for a new site. `addressType: 'Location'`
 * because sites in this tab are standalone facilities (depot, yard,
 * office) — distinct from the Org's main `Primary` address. Name +
 * code + service tags come from the editor draft so the address row
 * carries the same label / tags / reference code the operator typed
 * for the site.
 */
function toCreateAddressRequest(
  organisationId: string,
  form: SiteForm,
): CreateAddressRequest {
  return {
    organisationId,
    addressType: 'Location',
    name: form.address.name.trim(),
    code: form.address.code.trim() || null,
    fullAddress: form.address.fullAddress.trim(),
    city: form.address.city.trim() || null,
    postcode: form.address.postcode.trim() || null,
    countryIsoCode: form.address.countryIsoCode.trim().toUpperCase(),
    latitude: form.address.latitude,
    longitude: form.address.longitude,
    coordinatesSource: form.address.coordinatesSource ?? null,
    placeId: form.address.placeId.trim(),
    status: 'Active',
    serviceTags: form.address.serviceTags.trim() || null,
  };
}

/**
 * Build the PUT `/addresses` body. Echoes immutable server fields
 * (addressType, status) so the BE doesn't snap them to defaults on a
 * partial update. Name, code, and serviceTags now come from the form
 * since the editor exposes them.
 */
function toUpdateAddressRequest(
  original: Address,
  form: SiteForm,
): UpdateAddressRequest {
  return {
    addressId: original.id,
    addressType: original.addressType,
    name: form.address.name.trim(),
    code: form.address.code.trim() || null,
    fullAddress: form.address.fullAddress.trim(),
    city: form.address.city.trim() || null,
    postcode: form.address.postcode.trim() || null,
    countryIsoCode: form.address.countryIsoCode.trim().toUpperCase(),
    latitude: form.address.latitude,
    longitude: form.address.longitude,
    coordinatesSource: form.address.coordinatesSource ?? null,
    placeId: form.address.placeId.trim(),
    status: original.status,
    serviceTags: form.address.serviceTags.trim() || null,
  };
}

interface SiteEditorProps {
  isNew: boolean;
  draft: SiteForm;
  onChange: (next: SiteForm) => void;
  submitted: boolean;
  addressLoading: boolean;
  addressError: string | null;
  /**
   * True when the selected site is the org's main office. Switches the
   * editor to a simplified shape: just Name + Code + Service Tags, no
   * AddressInlineEditor, no Delete button. The address fields stay
   * pre-filled with the original values (via the address-arrival
   * effect upstream) so the BE update preserves them untouched.
   */
  isOrganisationSite: boolean;
  /** Service-tag library applicable to Locations / Sites. */
  applicableTags: ServiceTag[];
  /** Lower-cased set of currently selected tag names — for cheap chip-state lookup. */
  tagSet: Set<string>;
  onToggleTag: (tag: ServiceTag) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isBusy: boolean;
}

/**
 * Inline editor for a site — uses `AddressInlineEditor` with the full
 * "Addresses panel" feature set: name (which IS the site name), code,
 * address fields, embedded map, and a service-tag chip row. Visual
 * treatment matches the Teams editor (purple border + tinted bg).
 *
 * Address loading: in edit mode the address arrives via `useAddress`
 * — until then we show "Loading address…" and disable Save. The
 * AddressInlineEditor itself stays unmounted while loading so its
 * internal name field doesn't briefly flash empty.
 *
 * Why `mapPosition='bottom'`: the editor sits in a 420px-wide left
 * column, which is too narrow for a side-by-side form+map split. The
 * map stacks below the form fields so neither gets squashed.
 */
function SiteEditor({
  isNew,
  draft,
  onChange,
  submitted,
  addressLoading,
  addressError,
  isOrganisationSite,
  applicableTags,
  tagSet,
  onToggleTag,
  onSave,
  onCancel,
  onDelete,
  isBusy,
}: SiteEditorProps) {
  const { t } = useTranslation('organisation');
  const setAddress = (next: AddressDraft) => onChange({ ...draft, address: next });
  // Patch a single field on `draft.address` — used by the simplified
  // org-site editor where only name + code are typed inline.
  const setAddressField = <K extends keyof AddressDraft>(
    key: K,
    value: AddressDraft[K],
  ) => setAddress({ ...draft.address, [key]: value });

  const errs = {
    name: draft.address.name.trim().length === 0,
    fullAddress: draft.address.fullAddress.trim().length === 0,
    country: draft.address.countryIsoCode.trim().length === 0,
  };

  // Service-tag chip row. Built locally rather than pulled from a
  // shared component so the AddressInlineEditor (in @my/ui) stays
  // ignorant of the TMS `ServiceTag` type. Same chip palette + toggle
  // behaviour as the Locations editor.
  const tagsSlot =
    applicableTags.length === 0 ? null : (
      <div className="space-y-1 pt-1">
        <p className="text-xs font-medium text-slate-600">
          {t('siteAssignments.editor.tags')}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {applicableTags.map((tag) => {
            const isActive = tagSet.has(tag.name.toLowerCase());
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => onToggleTag(tag)}
                disabled={isBusy}
                title={tag.description ?? undefined}
                className={tagChipClass(isActive)}
              >
                {tag.name}
              </button>
            );
          })}
        </div>
      </div>
    );

  return (
    <div className="space-y-3 rounded-md border border-purple-200 bg-purple-50/40 p-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-slate-700">
        {isNew
          ? t('siteAssignments.detail.newTitle')
          : t('siteAssignments.detail.editTitleShort')}
      </h3>

      {addressLoading ? (
        <p className="text-xs text-slate-500">
          {t('siteAssignments.loadingAddress')}
        </p>
      ) : addressError ? (
        <p className="text-xs text-red-600">{addressError}</p>
      ) : isOrganisationSite ? (
        // Org's main office — restrict the surface to Name + Code +
        // Service Tags. Address fields stay pre-filled in `draft.address`
        // (via the address-arrival effect upstream) so the BE update
        // round-trips them unchanged. A small notice points operators
        // at the Organisation page for any address edits.
        <div className="space-y-3">
          <TextInput
            label={t('siteAssignments.fields.name')}
            name="site-name"
            autoComplete="off"
            value={draft.address.name}
            maxLength={128}
            onChange={(e) => setAddressField('name', e.target.value)}
            error={submitted && errs.name}
            errorMessage={t('siteAssignments.validation.nameRequired')}
          />
          <TextInput
            label={t('siteAssignments.fields.code')}
            name="site-code"
            autoComplete="off"
            value={draft.address.code}
            maxLength={64}
            placeholder={t('siteAssignments.fields.codePlaceholder')}
            onChange={(e) => setAddressField('code', e.target.value)}
          />
          {tagsSlot}
          <p className="rounded-sm border border-blue-200 bg-blue-50 p-2 text-[11px] text-blue-800">
            {t('siteAssignments.detail.organisationSiteHint')}
          </p>
        </div>
      ) : (
        <AddressInlineEditor
          label={t('siteAssignments.fields.address')}
          value={draft.address}
          onChange={(next) =>
            setAddress({
              name: next.name ?? '',
              code: next.code ?? '',
              fullAddress: next.fullAddress,
              city: next.city ?? '',
              postcode: next.postcode ?? '',
              countryIsoCode: next.countryIsoCode,
              latitude: next.latitude ?? null,
              longitude: next.longitude ?? null,
              placeId: next.placeId,
              coordinatesSource: next.coordinatesSource ?? null,
              serviceTags: draft.address.serviceTags,
            })
          }
          countryOptions={COUNTRY_OPTIONS}
          hasCodeField
          hasNameField
          enablePlacesSearch
          showMap
          mapPosition="bottom"
          tagsSlot={tagsSlot}
          showSummaryInput={false}
          showFooter={false}
        />
      )}

      {/* Validation summary surfaces only after submit so an untouched
          form doesn't shout at the operator. The AddressInlineEditor
          marks required fields with `*` decorations on its labels but
          can't show error messages inline, so we list missing fields
          here as a fallback. */}
      {submitted && (errs.name || errs.fullAddress || errs.country) && (
        <ul className="space-y-0.5 text-[11px] text-red-600">
          {errs.name && <li>{t('siteAssignments.validation.nameRequired')}</li>}
          {errs.fullAddress && (
            <li>{t('siteAssignments.validation.fullAddressRequired')}</li>
          )}
          {errs.country && (
            <li>{t('siteAssignments.validation.countryRequired')}</li>
          )}
        </ul>
      )}

      <p className="text-[11px] text-slate-500">
        {t('siteAssignments.detail.editHint')}
      </p>

      <div className="flex items-center justify-between gap-1">
        <div>
          {/* Delete is suppressed on the org's main office site — its
              lifecycle belongs to the Organisation page (deleting it
              here would orphan the org's `mainOfficeAddressId`). */}
          {onDelete && !isOrganisationSite && (
            <button
              type="button"
              aria-label={t('siteAssignments.ariaDelete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('siteAssignments.ariaCancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('siteAssignments.ariaSave')}
            onClick={onSave}
            disabled={isBusy || addressLoading}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tag-chip styling helper. Amber palette + pill shape, matching the
 * Locations editor so operators see the same visual language for
 * service tags everywhere they edit them.
 */
function tagChipClass(active: boolean): string {
  const base =
    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50';
  return `${base} ${
    active
      ? 'border-amber-600 bg-amber-600 text-white'
      : 'border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-500 hover:text-amber-950'
  }`;
}

// Built once at module scope — the underlying COUNTRIES catalogue is
// static, so there's no benefit to memoising per-render.
const COUNTRY_OPTIONS: SelectInputOption[] = COUNTRIES.map((c) => ({
  value: c.isoCode,
  label: `${c.flagEmoji} ${c.name}`,
}));
